<template>
  <div class="grid-container">
    <section>
      <div class="section-header section-header--xl section-header--dividing mt-3">
        <h1 class="heading" qaid="warranty-header">{{ bp('serviceClaims') }}</h1>
        <trek-link
          v-if="!isFeedbackActive"
          button
          secondary
          small
          qaid="warranty-start-new-claim"
          :href="`${contextPath}/warranty-claim`"
        >
          <span v-html="bp('startNewClaim')"></span>
        </trek-link>
      </div>
      <p v-if="!isFeedbackActive" class="mt-1" qaid="warranty-sub-header-text">{{ bp('provideImage') }}</p>
    </section>
    <!----------------------------------------New View with Product feedback----------------------------------------------------------->
    <section v-if="isFeedbackActive">
      <div class="grid-x grid-margin-x">
        <div
          class="cell large-6 flex flex-col border-solid border border-gray-10 items-center align-center text-center shadow py-5 mb-2 lg:mb-0"
        >
          <i class="material-icons md-48 mb-1 text-blue"> sync_problem </i>
          <span class="mb-2" v-html="bp('warrantyClaim')"></span>
          <trek-link button primary qaid="warranty-start-new-claim" :href="`${contextPath}/warranty-claim`">
            <span v-html="bp('startNewClaim')"></span>
          </trek-link>
        </div>
        <div
          class="cell large-6 flex flex-col border-solid border border-gray-10 items-center align-center text-center shadow py-5"
        >
          <i class="material-icons md-48 mb-1 text-orange"> feedback </i>
          <span v-html="bp('productFeedbackMsg1')"></span>
          <span class="mb-2" v-html="bp('productFeedbackMsg2')"></span>
          <trek-link button primary qaid="start-product__feedback" :href="`${contextPath}/product-feedback`">
            <span v-html="bp('submitProduct')"></span>
          </trek-link>
        </div>
      </div>
    </section>

    <!----------------------------------------------------------------------------------------------------------------------->
    <section>
      <div class="section-header section-header--md section-header--dividing mt-3">
        <h3 class="heading" qaid="warranty-search-header">{{ bp('searchClaims') }}</h3>
      </div>

      <div class="grid-x grid-margin-x">
        <div class="cell large-4">
          <div class="form-group">
            <label class="form-label" qaid="warranty-date-range-label">{{ bp('dateRange') }}</label>
            <date-picker
              ref="dateRangePicker"
              mode="range"
              :allow-input="true"
              qaid="warranty-search-date"
              @change="dateFilterInput"
            />
          </div>
        </div>

        <div class="cell large-4">
          <div class="form-group">
            <label class="form-label">{{ bp('claimNumber') }}</label>
            <input
              v-model="options.externalClaimNumber"
              type="text"
              class="form-control"
              :maxlength="15"
              qaid="warranty-search-claim-number"
            />
          </div>
        </div>
        <div class="cell large-4">
          <div class="form-group">
            <label class="form-label">{{ bp('poNumber') }}</label>
            <input
              v-model="options.poNumber"
              type="text"
              class="form-control"
              :maxlength="30"
              qaid="warranty-search-po-number"
            />
          </div>
        </div>
        <div class="cell large-4">
          <div class="form-group">
            <label class="checkbox">
              <input v-model="options.createdByMe" type="checkbox" qaid="warranty-view-only-checkbox" />
              <span class="control-indicator"></span>
              {{ bp('viewOnly') }}
            </label>
          </div>
        </div>
      </div>

      <div class="buttons">
        <trek-button primary qaid="warranty-search-button" @click="fetchAllClaims">
          <span v-html="bp('search')"></span>
        </trek-button>
        <trek-button secondary qaid="warranty-search-clear-filters" @click="clearFilters">
          <span v-html="bp('clearFilters')"></span>
        </trek-button>
      </div>
    </section>

    <section class="mt-4 relative">
      <tabs :active-name="tabSelected" @tab-click="tabSelected = $event.name">
        <tab-pane :label="bp('temporaryClaims')" qaid="warranty-tab-temporary" name="claimsSaved">
          <warranty-grid
            :base-properties="baseProperties"
            :size="pageSize"
            :loading="loadingSavedClaims"
            :grid-data="claimsSaved"
            grid-type="enumClaimStatusSaved"
            @delete-claim="openConfirmDeleteDialog"
            @page-change="pageChange"
          />
        </tab-pane>

        <tab-pane :label="bp('actionRequired')" name="claimsActionRequired">
          <warranty-grid
            :base-properties="baseProperties"
            :size="pageSize"
            :loading="loadingDIClaims"
            :grid-data="claimsActionRequired"
            grid-type="enumClaimStatusActionRequired"
            @page-change="pageChange"
          />
        </tab-pane>
        <tab-pane :label="bp('activeClaims')" name="claimsSubmitted">
          <warranty-grid
            :base-properties="baseProperties"
            :size="pageSize"
            :loading="loadingDIClaims"
            :grid-data="claimsSubmitted"
            grid-type="enumClaimStatusSubmitted"
            @page-change="pageChange"
          />
        </tab-pane>
        <tab-pane :label="bp('resolvedClaims')" name="claimsCompleted">
          <warranty-grid
            :base-properties="baseProperties"
            :size="pageSize"
            :loading="loadingDIClaims"
            :grid-data="claimsCompleted"
            grid-type="enumClaimStatusCompleted"
            :is-details-page-active="isDetailsPageActive"
            @page-change="pageChange"
          />
        </tab-pane>
      </tabs>
    </section>

    <el-dialog
      :visible.sync="dialogVisible"
      :title="bp('deleteConfirmCheck')"
      :center="true"
      :close-on-press-escape="false"
      :close-on-click-modal="false"
      :z-index="16000011"
      :close-dialog-tooltip="bp('closeDialogToolTip')"
      @close="resetDeleteData"
    >
      <div>{{ bp('claimNotSubmitted') }}</div>

      <template slot="footer">
        <div class="buttons buttons--center">
          <trek-button alert qaid="warranty-confirm-delete-button" @click="confirmDelete">
            <span v-html="bp('deleteConfirm')"></span>
          </trek-button>
          <trek-button secondary qaid="warranty-cancel-delete-button" @click="dialogVisible = false">
            <span v-html="bp('cancel')"></span>
          </trek-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import ElDialog from '@/components/Dialog';
import Tabs from '@/components/tabs/Tabs';
import TabPane from '@/components/tabs/TabPane';
import WarrantyGrid from '@/components/containers/retailer/WarrantyGrid';
import DatePicker from '@/components/DatePicker';
import TrekButton from '@/components/TrekButton';
import TrekLink from '@/components/TrekLink';
import {mapState, mapActions} from 'vuex';

export default {
  components: {
    ElDialog,
    Tabs,
    TabPane,
    WarrantyGrid,
    DatePicker,
    TrekButton,
    TrekLink,
  },
  props: {
    baseProperties: {
      type: Array,
      default() {
        return [];
      },
    },
    isFeedbackActive: {
      type: Boolean,
      default: false,
    },
    isDetailsPageActive: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      dialogVisible: false,
      claimsActionRequired: {claims: [], paging: {}, statusCode: '', statusName: ''},
      claimsCompleted: {claims: [], paging: {}, statusCode: '', statusName: ''},
      claimsSaved: {claims: [], paging: {}, statusCode: '', statusName: ''},
      claimsSubmitted: {claims: [], paging: {}, statusCode: '', statusName: ''},
      claimToBeDeleted: {claims: [], paging: {}, statusCode: '', statusName: ''},
      pageSize: ACC.warrantyClaimsPageSize || 50,
      contextPath: ACC.config.encodedContextPath || '',
      loadingSavedClaims: true,
      loadingDIClaims: true,
      options: {},
      tabSelected: 'claimsSaved',
    };
  },

  computed: {
    loading() {
      return this.loadingSavedClaims && this.loadingDIClaims;
    },

    ...mapState('storage', ['feedbackMsg']),
  },

  mounted() {
    if (this.feedbackMsg) {
      this.$notify({
        type: 'success',
        message: this.feedbackMsg,
      });
      this.setFeedbackMsg('');
    }
  },

  created() {
    this.fetchAllClaims();
  },

  methods: {
    ...mapActions('storage', ['setFeedbackMsg']),
    fetchAllClaims() {
      const promises = [this.fetchSavedClaims(), this.fetchDIClaims('all', 0)];

      axios.all(promises).then(() => {
        let claimsData = {
          claimsSaved: this.claimsSaved && this.claimsSaved.claims,
          claimsActionRequired: this.claimsActionRequired && this.claimsActionRequired.claims,
          claimsSubmitted: this.claimsSubmitted && this.claimsSubmitted.claims,
          claimsCompleted: this.claimsCompleted && this.claimsCompleted.claims,
        };

        if (!claimsData[this.tabSelected]) {
          for (let i in claimsData) {
            if (claimsData[i]) {
              this.tabSelected = i;
              break;
            }
          }
        }
      });
    },

    fetchSavedClaims() {
      this.loadingSavedClaims = true;

      return axios
        .get('/claims/savedClaims', {
          params: {
            startDate: isEmpty(this.options.startDate) ? null : this.options.startDate,
            endDate: isEmpty(this.options.endDate) ? null : this.options.endDate,
            externalClaimNumber: isEmpty(this.options.externalClaimNumber) ? null : this.options.externalClaimNumber,
            poNumber: isEmpty(this.options.poNumber) ? null : this.options.poNumber,
            createdByMe: this.options.createdByMe ? true : null,
          },
        })
        .then((response) => {
          if (response && response.data && response.data.data) {
            this.claimsSaved = response.data.data.enumClaimStatusSaved;
          } else {
            this.claimsSaved = null;
          }
        })
        .finally(() => (this.loadingSavedClaims = false));
    },

    fetchDIClaims(gridType, page) {
      this.loadingDIClaims = true;

      return axios
        .get('/claims/diClaims', {
          params: {
            pageIndex: page,
            pageSize: this.pageSize,
            startDate: isEmpty(this.options.startDate) ? null : this.options.startDate,
            endDate: isEmpty(this.options.endDate) ? null : this.options.endDate,
            externalClaimNumber: isEmpty(this.options.externalClaimNumber) ? null : this.options.externalClaimNumber,
            poNumber: isEmpty(this.options.poNumber) ? null : this.options.poNumber,
            createdByMe: this.options.createdByMe ? true : null,
          },
        })
        .then((response) => {
          if (response && response.data && response.data.data) {
            if (gridType === 'all' || gridType === 'enumClaimStatusCompleted') {
              this.claimsCompleted = response.data.data.enumClaimStatusCompleted;
            }

            if (gridType === 'all' || gridType === 'enumClaimStatusSubmitted') {
              this.claimsSubmitted = response.data.data.enumClaimStatusSubmitted;
            }

            if (gridType === 'all' || gridType === 'enumClaimStatusActionRequired') {
              this.claimsActionRequired = response.data.data.enumClaimStatusActionRequired;
            }
          } else {
            this.claimsCompleted = null;
            this.claimsSubmitted = null;
            this.claimsActionRequired = null;
          }
        })
        .finally(() => (this.loadingDIClaims = false));
    },

    pageChange({gridType, pageNumber}) {
      // Only fetch data when a di grid changes pages
      if (gridType !== 'enumClaimStatusSaved') {
        this.fetchDIClaims(gridType, pageNumber);
      }
    },

    dateFilterInput({startDate, endDate}) {
      if (startDate) {
        this.options.startDate = window.flatpickr.formatDate(startDate, 'm/d/Y');
      }
      if (endDate) {
        this.options.endDate = window.flatpickr.formatDate(endDate, 'm/d/Y');
      }
    },

    clearFilters() {
      this.options = {};
      this.$refs.dateRangePicker.fp.clear();
      this.fetchAllClaims();
    },

    openConfirmDeleteDialog(payload) {
      this.dialogVisible = true;
      this.claimToBeDeleted = payload;
    },

    // This is called when the confirmation dialog closes
    resetDeleteData() {
      this.dialogVisible = false;
      this.claimToBeDeleted = null;
    },

    confirmDelete() {
      this.dialogVisible = false;

      axios.delete(`/claims/delete/${this.claimToBeDeleted}`).then(() => {
        this.fetchSavedClaims();
      });
    },
  },
};
</script>
